import React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { DegreesBySchoolByQueryData } from "../../../graphql/generated/graphql";
import TableControlsContainer from "../../../containers/general/TableControls/TableControlsContainer";

interface DegreeOverviewProps {
  degreesData: DegreesBySchoolByQueryData;
  onDegreeSelect: (degreeId: string) => void;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
}

const DegreeOverview: React.FC<DegreeOverviewProps> = ({
  degreesData,
  onDegreeSelect,
  isLoading,
  page,
  setPage,
  pageSize,
  setPageSize,
}) => {
  if (isLoading) {
    return (
      <View style={styles.container}>
        <CustomText text="Loading degree programs..." size="m" useTranslationText={false} />
      </View>
    );
  }

  if (degreesData.degrees.length === 0) {
    return (
      <View style={styles.container}>
        <CustomText
          text="No degree programs found. Please ensure that degree programs are created in the system."
          size="m"
          useTranslationText={false}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.degreeGrid}>
        {degreesData.degrees.map((degree) => (
          <TouchableOpacity key={degree.id} style={styles.degreeCard} onPress={() => onDegreeSelect(degree.id)}>
            <View style={styles.degreeCardContent}>
              <CustomText
                text={degree.name}
                weight="bold"
                size="l"
                useTranslationText={false}
                style={styles.degreeTitle}
              />

              {degree.description && (
                <CustomText
                  text={degree.description}
                  size="s"
                  useTranslationText={false}
                  style={styles.degreeDescription}
                />
              )}

              <CustomSpacing type="vertical" size="m" />

              <CustomText text="Tap to view details →" size="s" style={styles.actionText} useTranslationText={false} />
            </View>
          </TouchableOpacity>
        ))}
      </View>
      {degreesData.totalCount > 0 && (
        <TableControlsContainer
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={degreesData.totalCount}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  degreeGrid: {
    // @ts-expect-error - works in web
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(300px, 1fr))",
    gap: 16,
  },
  degreeCard: {
    flex: 1,
    minWidth: 300,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e5e5",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  degreeCardContent: {
    padding: 16,
  },
  degreeTitle: {
    marginBottom: 8,
  },
  degreeDescription: {
    color: "#666",
    marginBottom: 12,
  },
  actionText: {
    color: "#0084FF",
    fontStyle: "italic",
  },
});

export default DegreeOverview;
